import Axios, { AxiosResponse } from "axios";
import AxiosDefaultRequestConfig from '@/AxiosDefaultRequestConfig';
import AxiosPostProcessor from '@/AxiosPostProcessor';
import WahooSaveRequest from '@/WahooSaveRequest';
import QueryStringUtility from './QueryStringUtility';
import WahooIntegrationStatus from './WahooIntegrationStatus';
import CloudSaveReadModel from './CloudSaveReadModel';
import WahooRoute from './WahooRoute';

const wahooSaveAfterLinkSessionKey = 'wahooSaveAfterLink';
const wahooLinkRequiredPrefix = 'WahooLinkRequired=';

function wahooRoutesBaseUrl() {
    const baseUrl = process.env.VUE_APP_WAHOO_ROUTES_BASE_URL || process.env.VUE_APP_BASE_URL_API || ''
    return baseUrl.endsWith('/') ? baseUrl.substring(0, baseUrl.length - 1) : baseUrl
}

export class WahooServiceStatic {
    beginLinkWithWahooUrl(state?: string) {
        return `${wahooRoutesBaseUrl()}/api/wahoo/link${QueryStringUtility.getQueryString({state})}`;
    }

    rememberSaveAfterLink() {
        try {
            sessionStorage.setItem(wahooSaveAfterLinkSessionKey, 'true')
        } catch {
            // Ignore storage failures; the OAuth state parameter is still sent.
        }
    }

    forgetSaveAfterLink() {
        try {
            sessionStorage.removeItem(wahooSaveAfterLinkSessionKey)
        } catch {
            // Ignore storage failures.
        }
    }

    isSaveAfterLinkRequested() {
        try {
            return sessionStorage.getItem(wahooSaveAfterLinkSessionKey) === 'true'
        } catch {
            return false
        }
    }

    linkWithWahoo(code: string): Promise<AxiosResponse<void>> {
        const url = `${wahooRoutesBaseUrl()}/api/wahoo/link${QueryStringUtility.getQueryString({code})}`
        return AxiosPostProcessor.postProcess(Axios.post(url, undefined, AxiosDefaultRequestConfig.merge({ })));
    }

    getIntegrationStatus(): Promise<AxiosResponse<WahooIntegrationStatus>> {
        const url = `${wahooRoutesBaseUrl()}/api/wahoo/integration`
        return AxiosPostProcessor.postProcess(Axios.get<WahooIntegrationStatus>(url, AxiosDefaultRequestConfig.merge({ })));
    }

    getRoutes(): Promise<AxiosResponse<WahooRoute[]>> {
        const url = `${wahooRoutesBaseUrl()}/api/wahoo/routes`
        return AxiosPostProcessor.postProcess(Axios.get<WahooRoute[]>(url, AxiosDefaultRequestConfig.merge({ })));
    }

    getRoute(routeId: string): Promise<AxiosResponse<CloudSaveReadModel>> {
        const url = `${wahooRoutesBaseUrl()}/api/wahoo/routes/${encodeURIComponent(routeId)}`
        return AxiosPostProcessor.postProcess(Axios.get<CloudSaveReadModel>(url, AxiosDefaultRequestConfig.merge({ })));
    }

    deleteRoute(routeId: string): Promise<AxiosResponse<void>> {
        const url = `${wahooRoutesBaseUrl()}/api/wahoo/routes/${encodeURIComponent(routeId)}`
        return AxiosPostProcessor.postProcess(Axios.delete(url, AxiosDefaultRequestConfig.merge({ })));
    }

    updateRoute(routeId: string, saveRequest: WahooSaveRequest): Promise<AxiosResponse<void>> {
        const url = `${wahooRoutesBaseUrl()}/api/wahoo/routes/${encodeURIComponent(routeId)}`
        return AxiosPostProcessor.postProcess(Axios.put(url, saveRequest, AxiosDefaultRequestConfig.merge({ })));
    }

    unlinkFromWahoo(): Promise<AxiosResponse<void>> {
        const url = `${wahooRoutesBaseUrl()}/api/wahoo/link`
        return AxiosPostProcessor.postProcess(Axios.delete(url, AxiosDefaultRequestConfig.merge({ })));
    }

    saveToWahoo(saveRequest: WahooSaveRequest): Promise<AxiosResponse<void>> {
        const url = `${wahooRoutesBaseUrl()}/api/wahoo/routes`
        return AxiosPostProcessor.postProcess(Axios.post(url, saveRequest, AxiosDefaultRequestConfig.merge({ })));
    }

    linkRequiredUrlFromSaveError(error: any) {
        const responseData = error?.response?.data
        if (typeof responseData === 'string' && responseData.startsWith(wahooLinkRequiredPrefix)) {
            return responseData.substring(wahooLinkRequiredPrefix.length)
        }
        if (responseData && typeof responseData.url === 'string') {
            return responseData.url
        }
        return null
    }

    authRequiredFromSaveError(error: any) {
        const responseData = error?.response?.data
        const code = responseData?.code
        if (code === 'wahoo_connect_required' || code === 'wahoo_reconnect_required') {
            return {
                code,
                url: typeof responseData.url === 'string' ? responseData.url : null
            }
        }

        const legacyUrl = this.linkRequiredUrlFromSaveError(error)
        return legacyUrl
            ? { code: 'wahoo_connect_required', url: legacyUrl }
            : null
    }
}

const WahooService = new WahooServiceStatic();
export default WahooService;
